import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Pagination,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  PlayCircleOutline as PlayCircleOutlineIcon,
  VerifiedOutlined as VerifiedOutlinedIcon,
  CancelOutlined as CancelOutlinedIcon,
} from '@mui/icons-material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import {
  api,
  type PodcastVideoRecord,
  type TopViewingSitePlan,
} from '../../services/api';
import {
  PodcastVideoPlayerDialog,
  formatCreatedDate,
  getSourceMeta,
  sourceFromUrl,
  type PodcastSource,
} from '../../components/podcast/PodcastVideosTab';

// ---------------------------------------------------------------------------
// Manage Podcast Videos — admin-only dialog
//
// Three tabs:
//   - Pending (default): rows still awaiting a decision. Each card
//     carries a checkbox; the bulk actions verify OR reject every
//     selected ID in one POST and move them into the appropriate tab
//     on success. Rejection requires a reason.
//   - Verified: rows the admin has approved. Read-only here.
//   - Rejected: rows the admin has rejected, with the reason
//     visible on each card. Read-only here — rejection is terminal.
//
// The dialog deliberately surfaces nothing else (no edit, no delete,
// no project re-tagging) — those workflows live in My Podcasts.
// ---------------------------------------------------------------------------

interface ManagePodcastVideosDialogProps {
  open: boolean;
  onClose: () => void;
  /** Training type filter and Analytics tab are admin-only. */
  isAdmin?: boolean;
}

type StatusTab = 'pending' | 'verified' | 'rejected';
// Full tab set surfaced by the dialog. `analytics` is a sibling tab
// that doesn't correspond to a row status — it stays out of
// `StatusTab` so the existing per-status caches / loaders don't have
// to invent a no-op entry for it.
type DialogTab = StatusTab | 'analytics';

const CARD_THUMB_HEIGHT = 140;
// 12 rows per page = 4 rows × 3 cols on desktop. Strikes a balance
// between scrollability and one round-trip to render.
const PAGE_SIZE = 12;

// ---------------------------------------------------------------------------
// Analytics tab — read-only dashboard built from the same per-status caches
// the row tabs use, so the dialog never makes additional network calls.
// Charts use `recharts` to match `AnalyticsDashboardDialog`'s look-and-feel.
// ---------------------------------------------------------------------------

// Status pie palette — amber/teal/red so each slice reads quickly without
// having to scan the legend. Matches the semantic intent of the row tabs
// (pending = "waiting", verified = "approved", rejected = "blocked").
const STATUS_COLORS: Record<'pending' | 'verified' | 'rejected', string> = {
  pending: '#F6AD55',
  verified: '#38B2AC',
  rejected: '#E53E3E',
};

// Brand colours for each video provider. Re-used by the platform line
// graph below and the pie's legend swatches so a viewer can match a line
// to a platform without reading the tooltip.
//
// TikTok's primary brand is near-black (invisible on dark) and its
// secondary magenta clashes with RedNote's red. We use TikTok's
// official cyan accent (#25F4EE) instead — distinct from every other
// platform colour in the palette and legible on both light and dark
// backgrounds.
function getPlatformColors(_mode: 'light' | 'dark'): Record<PodcastSource, string> {
  return {
    youtube: '#FF0000',
    tiktok: '#25F4EE',
    instagram: '#E1306C',
    facebook: '#1877F2',
    rednote: '#FF2442',
  };
}

// Generic accent palette for the top-projects / top-creators bar charts.
// Distinct from PLATFORM_COLORS so a project sharing a colour with a
// platform doesn't read as "this project is on YouTube".
const ACCENT_COLORS = [
  '#6366F1', // indigo
  '#06B6D4', // cyan
  '#F59E0B', // amber
  '#10B981', // emerald
  '#EC4899', // pink
  '#8B5CF6', // violet
  '#EF4444', // red
  '#14B8A6', // teal
  '#F97316', // orange
  '#3B82F6', // blue
];

// Card chrome shared by every analytics tile. Mirrors the
// `chartCardSx` used in AnalyticsDashboardDialog so the visual
// language is consistent across admin dashboards.
const analyticsCardSx = {
  p: 2.5,
  bgcolor: 'rgba(var(--color-text-rgb), 0.03)',
  border: '1px solid rgba(var(--color-text-rgb), 0.08)',
  borderRadius: 2,
  height: '100%',
} as const;

// Truncate long labels so the chart axes don't blow out horizontally.
function truncateLabel(value: string, max: number): string {
  if (!value) return '';
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

// Convert an ISO timestamp into a `YYYY-MM-DD` bucket. Used by the
// platform line graph's X-axis. Returns `null` when the input is
// missing or unparseable so the caller can skip the row.
function dayKey(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

// Map a video record to its `PodcastSource` bucket. Falls back to a
// URL-based lookup when the row's `videoSource` string doesn't match
// one of the known labels (older rows / unusual casing).
function resolveSource(video: PodcastVideoRecord): PodcastSource | null {
  const lower = (video.videoSource || '').trim().toLowerCase();
  if (lower === 'youtube') return 'youtube';
  if (lower === 'tiktok') return 'tiktok';
  if (lower === 'instagram') return 'instagram';
  if (lower === 'facebook') return 'facebook';
  if (lower === 'rednote') return 'rednote';
  return sourceFromUrl(video.videoUrl);
}

// Shared dark tooltip used by every chart on the analytics tab.
type AnalyticsTooltipPayload = Array<{
  value?: number | string | null;
  color?: string;
  name?: string | number;
  payload?: Record<string, unknown>;
}>;

interface AnalyticsTooltipProps {
  active?: boolean;
  label?: string | number;
  payload?: AnalyticsTooltipPayload;
  /** Header label rendered above the value rows (e.g. "Month", "Status"). */
  titleLabel?: string;
}

const AnalyticsTooltip: React.FC<AnalyticsTooltipProps> = ({
  active,
  label,
  payload,
  titleLabel,
}) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper
      elevation={0}
      sx={{
        px: 1.5,
        py: 1,
        borderRadius: 1.5,
        bgcolor: 'rgba(20, 28, 43, 0.96)',
        border: '1px solid rgba(255,255,255,0.1)',
        boxShadow: '0 12px 28px rgba(0,0,0,0.3)',
      }}
    >
      {titleLabel != null && (
        <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.72)' }}>
          {titleLabel}
        </Typography>
      )}
      <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff' }}>
        {/* Charts driven by a category axis (line / bar) pass `label`;
            slice-based charts (pie) don't, so fall back to the first
            payload entry's name so the heading shows e.g. "Verified"
            instead of an empty placeholder. */}
        {label != null && label !== ''
          ? String(label)
          : String(payload[0]?.name ?? '-')}
      </Typography>
      <Stack spacing={0.5} sx={{ mt: 1 }}>
        {payload.map((entry, idx) => {
          const raw = entry?.value;
          const num = typeof raw === 'number' ? raw : Number(raw || 0);
          return (
            <Box
              key={`${String(entry?.name ?? 'value')}-${idx}`}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
            >
              <Typography variant="caption" sx={{ color: entry?.color || 'rgba(255,255,255,0.72)' }}>
                {String(entry?.name ?? 'Count')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, color: entry?.color || '#63B3ED' }}>
                {Number.isFinite(num) ? num.toLocaleString() : '-'}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
};

// Card title + optional subtitle. Centralised so every tile shares the
// same vertical rhythm.
const AnalyticsCardHeader: React.FC<{ title: string; subtitle?: string }> = ({
  title,
  subtitle,
}) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography
      variant="subtitle2"
      sx={{
        fontWeight: 700,
        color: 'var(--color-text-strong)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        fontSize: '0.78rem',
      }}
    >
      {title}
    </Typography>
    {subtitle && (
      <Typography
        variant="caption"
        sx={{ display: 'block', mt: 0.25, color: 'var(--color-text-muted)' }}
      >
        {subtitle}
      </Typography>
    )}
  </Box>
);

interface PodcastAnalyticsPanelProps {
  pendingRows: PodcastVideoRecord[];
  verifiedRows: PodcastVideoRecord[];
  rejectedRows: PodcastVideoRecord[];
  loading: boolean;
  isAdmin?: boolean;
}

const PodcastAnalyticsPanel: React.FC<PodcastAnalyticsPanelProps> = ({
  pendingRows: rawPending,
  verifiedRows: rawVerified,
  rejectedRows: rawRejected,
  loading,
  isAdmin = false,
}) => {
  const [section, setSection] = useState<'podcast' | 'training'>('podcast');

  useEffect(() => {
    if (!isAdmin && section === 'training') setSection('podcast');
  }, [isAdmin, section]);

  const pendingRows = useMemo(
    () => rawPending.filter((v) => (v.videoType ?? 'podcast') === section),
    [rawPending, section],
  );
  const verifiedRows = useMemo(
    () => rawVerified.filter((v) => (v.videoType ?? 'podcast') === section),
    [rawVerified, section],
  );
  const rejectedRows = useMemo(
    () => rawRejected.filter((v) => (v.videoType ?? 'podcast') === section),
    [rawRejected, section],
  );

  const muiTheme = useMuiTheme();
  const chartGridStroke =
    muiTheme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.14)'
      : 'rgba(31,41,68,0.12)';
  const chartAxisStroke =
    muiTheme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.3)'
      : 'rgba(31,41,68,0.24)';
  const chartTickStyle = useMemo(
    () => ({ fill: muiTheme.palette.text.secondary, fontSize: 12 }),
    [muiTheme.palette.text.secondary],
  );
  const platformColors = useMemo(
    () => getPlatformColors(muiTheme.palette.mode === 'dark' ? 'dark' : 'light'),
    [muiTheme.palette.mode],
  );

  // ── Drill-down state ────────────────────────────────────────────────
  // Clicking a project bar or a creator row opens a list dialog of the
  // matching videos. We keep the context (kind + id + display label)
  // here and derive the video list via `useMemo` so the dialog is a
  // pure projection of the existing per-status caches.
  const [drillContext, setDrillContext] = useState<
    | { kind: 'project'; id: string; label: string }
    | { kind: 'creator'; id: string; label: string }
    | { kind: 'status'; status: 'pending' | 'verified' | 'rejected' | 'all'; label: string }
    | null
  >(null);

  const drillVideos = useMemo<PodcastVideoRecord[]>(() => {
    if (!drillContext) return [];
    // Project / creator drills are verified-only to match the chart
    // counts that originate them. Status-card drills mirror whichever
    // status the card represents (the Total card returns all three).
    if (drillContext.kind === 'project') {
      return verifiedRows.filter((row) =>
        (row.projects ?? []).some((p) => p.id === drillContext.id),
      );
    }
    if (drillContext.kind === 'creator') {
      return verifiedRows.filter(
        (row) => (row.createdBy || '').trim() === drillContext.id,
      );
    }
    switch (drillContext.status) {
      case 'pending':
        return pendingRows;
      case 'verified':
        return verifiedRows;
      case 'rejected':
        return rejectedRows;
      case 'all':
      default:
        return [...verifiedRows, ...pendingRows, ...rejectedRows];
    }
  }, [drillContext, pendingRows, verifiedRows, rejectedRows]);

  // ── 1. Status distribution (pie) ─────────────────────────────────────
  const statusData = useMemo(
    () => [
      { key: 'pending', label: 'Pending', value: pendingRows.length },
      { key: 'verified', label: 'Verified', value: verifiedRows.length },
      { key: 'rejected', label: 'Rejected', value: rejectedRows.length },
    ],
    [pendingRows.length, verifiedRows.length, rejectedRows.length],
  );
  const statusTotal = statusData.reduce((sum, d) => sum + d.value, 0);

  // ── 2. Verified videos by platform over time (line) ──────────────────
  // A breakdown toggle filters the underlying verified set to those
  // recorded in the company podcast room (`isPodcastRoom === true`),
  // those recorded elsewhere, or all of them. Lets an admin see how
  // much of the verified pipeline is room-sourced over time.
  const [roomFilter, setRoomFilter] = useState<'all' | 'room' | 'other'>('all');

  const platformVerifiedRows = useMemo(() => {
    if (section === 'training' || roomFilter === 'all') return verifiedRows;
    if (roomFilter === 'room') return verifiedRows.filter((r) => r.isPodcastRoom === true);
    return verifiedRows.filter((r) => r.isPodcastRoom !== true);
  }, [verifiedRows, roomFilter, section]);

  // X-axis: days (`YYYY-MM-DD`) bucketed by each video's `createdAt`
  // (i.e. when the clip was submitted), spanning from the earliest to
  // the most recent verified row, so days with zero submissions still
  // render as a 0 — keeping the line graph honest about gaps in
  // activity. Only verified rows are counted, so the chart reads as
  // "creation trend of videos that ended up verified".
  const platformLineSeries = useMemo(() => {
    if (platformVerifiedRows.length === 0) {
      return { days: [] as string[], data: [] as Array<Record<string, number | string>>, platforms: [] as PodcastSource[] };
    }

    const platformsPresent = new Set<PodcastSource>();
    const buckets = new Map<string, Map<PodcastSource, number>>();
    let minDay: string | null = null;
    let maxDay: string | null = null;

    for (const row of platformVerifiedRows) {
      const src = resolveSource(row);
      if (!src) continue;
      platformsPresent.add(src);
      const d = dayKey(row.createdAt);
      if (!d) continue;
      if (!minDay || d < minDay) minDay = d;
      if (!maxDay || d > maxDay) maxDay = d;
      let inner = buckets.get(d);
      if (!inner) {
        inner = new Map<PodcastSource, number>();
        buckets.set(d, inner);
      }
      inner.set(src, (inner.get(src) ?? 0) + 1);
    }

    if (!minDay || !maxDay) {
      return { days: [], data: [], platforms: [] };
    }

    // Walk every day from min → max (inclusive) so the X-axis is
    // continuous even when a particular day has zero verifications.
    // We iterate via `Date` UTC arithmetic so DST / month boundaries
    // never produce duplicated or skipped buckets.
    const days: string[] = [];
    const startMs = Date.UTC(
      Number(minDay.slice(0, 4)),
      Number(minDay.slice(5, 7)) - 1,
      Number(minDay.slice(8, 10)),
    );
    const endMs = Date.UTC(
      Number(maxDay.slice(0, 4)),
      Number(maxDay.slice(5, 7)) - 1,
      Number(maxDay.slice(8, 10)),
    );
    const ONE_DAY = 24 * 60 * 60 * 1000;
    for (let t = startMs; t <= endMs; t += ONE_DAY) {
      const dt = new Date(t);
      const key = `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
      days.push(key);
    }

    const platforms = Array.from(platformsPresent);
    const data = days.map((d) => {
      const row: Record<string, number | string> = { day: d };
      for (const p of platforms) {
        row[p] = buckets.get(d)?.get(p) ?? 0;
      }
      return row;
    });

    return { days, data, platforms };
  }, [platformVerifiedRows]);

  // ── 3. Top projects tagged — verified videos only (horizontal bar) ──
  // Counts every (verified video × project) edge so the chart aligns
  // with the verified-only scope used elsewhere on this tab.
  const topProjects = useMemo(() => {
    const counts = new Map<string, { id: string; name: string; count: number }>();
    for (const row of verifiedRows) {
      for (const proj of row.projects ?? []) {
        const existing = counts.get(proj.id);
        if (existing) existing.count += 1;
        else counts.set(proj.id, { id: proj.id, name: proj.name || proj.id, count: 1 });
      }
    }
    return Array.from(counts.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)
      .map((entry) => ({ ...entry, label: truncateLabel(entry.name, 28) }));
  }, [verifiedRows]);

  // ── 4. Top creators (verified videos) ────────────────────────────────
  // Restricted to `verifiedRows` per the brief — "Users with the most
  // number of verified videos". Aggregates by `createdBy` (CEA) so two
  // submitters with the same display name don't collapse. A breakdown
  // toggle narrows the set to podcast-room / non-room verified videos.
  const [creatorRoomFilter, setCreatorRoomFilter] = useState<'all' | 'room' | 'other'>('all');

  const creatorVerifiedRows = useMemo(() => {
    if (section === 'training' || creatorRoomFilter === 'all') return verifiedRows;
    if (creatorRoomFilter === 'room') return verifiedRows.filter((r) => r.isPodcastRoom === true);
    return verifiedRows.filter((r) => r.isPodcastRoom !== true);
  }, [verifiedRows, creatorRoomFilter, section]);

  const topCreators = useMemo(() => {
    const byCea = new Map<
      string,
      { cea: string; name: string; photo: string | null; count: number }
    >();
    for (const row of creatorVerifiedRows) {
      const cea = (row.createdBy || '').trim() || '—';
      const name = (row.createdByName || '').trim() || cea;
      const existing = byCea.get(cea);
      if (existing) {
        existing.count += 1;
      } else {
        byCea.set(cea, {
          cea,
          name,
          photo: row.createdByPhoto || null,
          count: 1,
        });
      }
    }
    return Array.from(byCea.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [creatorVerifiedRows]);

  // ── 5. Most viewed video project tags — verified videos only (horizontal bar) ──
  const mostViewedProjects = useMemo(() => {
    const viewsMap = new Map<string, { id: string; name: string; views: number }>();
    for (const row of verifiedRows) {
      const viewsCount = Number(row.views) || 0;
      for (const proj of row.projects ?? []) {
        const existing = viewsMap.get(proj.id);
        if (existing) {
          existing.views += viewsCount;
        } else {
          viewsMap.set(proj.id, { id: proj.id, name: proj.name || proj.id, views: viewsCount });
        }
      }
    }
    return Array.from(viewsMap.values())
      .sort((a, b) => b.views - a.views)
      .slice(0, 10)
      .map((entry) => ({ ...entry, label: truncateLabel(entry.name, 28) }));
  }, [verifiedRows]);

  // ── 6. Most shared video project tags — verified videos only (horizontal bar) ──
  const mostSharedProjects = useMemo(() => {
    const sharesMap = new Map<string, { id: string; name: string; shares: number }>();
    for (const row of verifiedRows) {
      const sharesCount = Number(row.shares) || 0;
      for (const proj of row.projects ?? []) {
        const existing = sharesMap.get(proj.id);
        if (existing) {
          existing.shares += sharesCount;
        } else {
          sharesMap.set(proj.id, { id: proj.id, name: proj.name || proj.id, shares: sharesCount });
        }
      }
    }
    return Array.from(sharesMap.values())
      .sort((a, b) => b.shares - a.shares)
      .slice(0, 10)
      .map((entry) => ({ ...entry, label: truncateLabel(entry.name, 28) }));
  }, [verifiedRows]);

  if (loading && pendingRows.length === 0 && verifiedRows.length === 0 && rejectedRows.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2.5}>
      {/* Toggle Selector for Podcast vs Training Videos Analytics */}
      {isAdmin ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
        <ToggleButtonGroup
          size="medium"
          exclusive
          value={section}
          onChange={(_, value: 'podcast' | 'training' | null) => {
            if (value) setSection(value);
          }}
          aria-label="Toggle analytics section"
          sx={{
            bgcolor: 'rgba(var(--color-text-rgb), 0.03)',
            border: '1px solid rgba(var(--color-text-rgb), 0.08)',
            p: 0.5,
            borderRadius: 2,
            '& .MuiToggleButton-root': {
              border: 'none',
              borderRadius: '6px !important',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.85rem',
              px: 3,
              py: 0.75,
              color: 'var(--color-text-muted)',
              '&.Mui-selected': {
                bgcolor: 'var(--color-accent-soft)',
                color: 'var(--color-accent)',
                '&:hover': {
                  bgcolor: 'var(--color-accent-soft)',
                },
              },
            },
          }}
        >
          <ToggleButton value="podcast">Podcast Videos</ToggleButton>
          <ToggleButton value="training">Training Videos</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      ) : null}

      {/* Summary stat strip — quick at-a-glance totals before the
          reader engages with any chart. */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        {([
          { key: 'verified', label: 'Verified', value: verifiedRows.length },
          { key: 'pending', label: 'Pending', value: pendingRows.length },
          { key: 'rejected', label: 'Rejected', value: rejectedRows.length },
          { key: 'total', label: 'Total', value: statusTotal },
        ] as const).map((stat) => {
          const accent =
            stat.key === 'total'
              ? 'var(--color-accent)'
              : STATUS_COLORS[stat.key as 'pending' | 'verified' | 'rejected'];
          // `total` maps to the synthetic 'all' status so the drill
          // dialog returns the union of all three caches.
          const drillStatus: 'pending' | 'verified' | 'rejected' | 'all' =
            stat.key === 'total' ? 'all' : stat.key;
          const drillLabel =
            stat.key === 'total'
              ? 'All videos'
              : `${stat.label} videos`;
          const openDrill = () =>
            setDrillContext({
              kind: 'status',
              status: drillStatus,
              label: drillLabel,
            });
          return (
            <Box
              key={stat.key}
              role="button"
              tabIndex={0}
              onClick={openDrill}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openDrill();
                }
              }}
              sx={{
                ...analyticsCardSx,
                flex: 1,
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                cursor: 'pointer',
                transition: 'background-color 120ms ease-out, transform 120ms ease-out',
                '&:hover': {
                  bgcolor: 'rgba(var(--color-text-rgb), 0.06)',
                },
                '&:focus-visible': {
                  outline: '2px solid var(--color-accent)',
                  outlineOffset: 2,
                },
              }}
            >
              <Box
                sx={{
                  width: 8,
                  alignSelf: 'stretch',
                  borderRadius: 4,
                  bgcolor: accent,
                }}
              />
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'var(--color-text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    fontWeight: 600,
                  }}
                >
                  {stat.label}
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}
                >
                  {stat.value.toLocaleString()}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
        }}
      >
        {/* ── 1. Status pie ── */}
        <Box sx={analyticsCardSx}>
          <AnalyticsCardHeader
            title="Status Distribution"
            subtitle="Share of videos by review state"
          />
          {statusTotal === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
                No videos yet.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <RechartsTooltip
                    content={<AnalyticsTooltip titleLabel="Status" />}
                  />
                  <Legend
                    verticalAlign="bottom"
                    iconType="circle"
                    wrapperStyle={{ fontSize: 12 }}
                  />
                  <Pie
                    data={statusData}
                    dataKey="value"
                    nameKey="label"
                    innerRadius={60}
                    outerRadius={95}
                    paddingAngle={2}
                    label={({ value }) => {
                      const v = typeof value === 'number' ? value : 0;
                      const pct = statusTotal > 0 ? (v / statusTotal) * 100 : 0;
                      return pct >= 5 ? `${pct.toFixed(0)}%` : '';
                    }}
                    labelLine={false}
                  >
                    {statusData.map((slice) => (
                      <Cell
                        key={slice.key}
                        fill={STATUS_COLORS[slice.key as 'pending' | 'verified' | 'rejected']}
                        stroke="var(--color-bg)"
                        strokeWidth={2}
                      />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </Box>
          )}
        </Box>

        {/* ── 2. Verified videos by platform over time ── */}
        <Box sx={analyticsCardSx}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <AnalyticsCardHeader
              title="Video Creation Trend by Platform"
              subtitle="Daily count by creation date — verified videos only, one line per platform"
            />
            {section === 'podcast' && (
              <ToggleButtonGroup
                size="small"
                exclusive
                value={roomFilter}
                onChange={(_, value: 'all' | 'room' | 'other' | null) => {
                  if (value) setRoomFilter(value);
                }}
                aria-label="Filter by podcast room"
                sx={{
                  '& .MuiToggleButton-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.72rem',
                    px: 1.25,
                    py: 0.25,
                  },
                }}
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="room">Podcast room</ToggleButton>
                <ToggleButton value="other">Other</ToggleButton>
              </ToggleButtonGroup>
            )}
          </Box>
          {platformLineSeries.platforms.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
                {roomFilter === 'room' && section === 'podcast'
                  ? 'No verified podcast-room videos yet.'
                  : roomFilter === 'other' && section === 'podcast'
                    ? 'No verified non-podcast-room videos yet.'
                    : 'No verified videos yet.'}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={platformLineSeries.data}
                  margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} />
                  <XAxis
                    dataKey="day"
                    stroke={chartAxisStroke}
                    tick={chartTickStyle}
                    // Recharts auto-thins ticks via `interval` when
                    // data is dense; `preserveStartEnd` makes sure the
                    // very first and last day are always labelled.
                    interval="preserveStartEnd"
                    minTickGap={24}
                  />
                  <YAxis
                    allowDecimals={false}
                    stroke={chartAxisStroke}
                    tick={chartTickStyle}
                  />
                  <RechartsTooltip
                    content={<AnalyticsTooltip titleLabel="Day" />}
                  />
                  <Legend
                    iconType="circle"
                    wrapperStyle={{ fontSize: 12 }}
                  />
                  {platformLineSeries.platforms.map((p) => (
                    <Line
                      key={p}
                      type="monotone"
                      dataKey={p}
                      name={getSourceMeta(p).label}
                      stroke={platformColors[p]}
                      strokeWidth={2.25}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          )}
        </Box>

        {/* ── 3. Top projects tagged ── */}
        <Box sx={analyticsCardSx}>
          <AnalyticsCardHeader
            title="Top Projects Tagged"
            subtitle="Verified videos per project"
          />
          {topProjects.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
                No project tags yet.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ height: Math.max(280, topProjects.length * 32 + 40) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={topProjects}
                  layout="vertical"
                  margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} horizontal={false} />
                  <XAxis
                    type="number"
                    allowDecimals={false}
                    stroke={chartAxisStroke}
                    tick={chartTickStyle}
                  />
                  <YAxis
                    type="category"
                    dataKey="label"
                    width={150}
                    stroke={chartAxisStroke}
                    tick={chartTickStyle}
                  />
                  <RechartsTooltip
                    content={<AnalyticsTooltip titleLabel="Project" />}
                  />
                  <Bar
                    dataKey="count"
                    name="Tagged videos"
                    radius={[0, 6, 6, 0]}
                    // Bar click drills into the per-project list. Recharts
                    // passes the row payload as the first arg; we narrow
                    // to our shape before reading id/name.
                    cursor="pointer"
                    onClick={(payload: unknown) => {
                      const p = payload as { id?: string; name?: string } | undefined;
                      if (!p?.id) return;
                      setDrillContext({
                        kind: 'project',
                        id: p.id,
                        label: p.name || p.id,
                      });
                    }}
                  >
                    {topProjects.map((proj, idx) => (
                      <Cell key={proj.id} fill={ACCENT_COLORS[idx % ACCENT_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}
        </Box>

        {/* ── 4. Top creators (verified videos) ── */}
        <Box sx={analyticsCardSx}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <AnalyticsCardHeader
              title="Top Creators"
              subtitle="Submitters with the most verified videos"
            />
            {section === 'podcast' && (
              <ToggleButtonGroup
                size="small"
                exclusive
                value={creatorRoomFilter}
                onChange={(_, value: 'all' | 'room' | 'other' | null) => {
                  if (value) setCreatorRoomFilter(value);
                }}
                aria-label="Filter creators by podcast room"
                sx={{
                  '& .MuiToggleButton-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.72rem',
                    px: 1.25,
                    py: 0.25,
                  },
                }}
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="room">Podcast room</ToggleButton>
                <ToggleButton value="other">Other</ToggleButton>
              </ToggleButtonGroup>
            )}
          </Box>
          {topCreators.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
                {creatorRoomFilter === 'room' && section === 'podcast'
                  ? 'No users with verified podcast-room videos yet.'
                  : creatorRoomFilter === 'other' && section === 'podcast'
                    ? 'No users with verified non-podcast-room videos yet.'
                    : 'No users with verified videos yet.'}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1}>
              {topCreators.map((creator, idx) => {
                const accent = ACCENT_COLORS[idx % ACCENT_COLORS.length];
                const max = topCreators[0]?.count || 1;
                const pct = (creator.count / max) * 100;
                return (
                  <Box
                    key={creator.cea}
                    // Whole row is a click target — opens the per-creator
                    // verified-video drill dialog. Keyboard-accessible
                    // via `role=button` + Enter/Space handler.
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      setDrillContext({
                        kind: 'creator',
                        id: creator.cea,
                        label: creator.name,
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setDrillContext({
                          kind: 'creator',
                          id: creator.cea,
                          label: creator.name,
                        });
                      }
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25,
                      cursor: 'pointer',
                      borderRadius: 1,
                      px: 0.5,
                      py: 0.25,
                      transition: 'background-color 120ms ease-out',
                      '&:hover': {
                        bgcolor: 'rgba(var(--color-text-rgb), 0.05)',
                      },
                      '&:focus-visible': {
                        outline: '2px solid var(--color-accent)',
                        outlineOffset: 2,
                      },
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        width: 22,
                        textAlign: 'right',
                        color: 'var(--color-text-muted)',
                        fontWeight: 700,
                      }}
                    >
                      {idx + 1}
                    </Typography>
                    <Avatar
                      src={creator.photo || undefined}
                      alt={creator.name}
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: accent,
                        color: '#fff',
                        fontSize: '0.85rem',
                        fontWeight: 700,
                      }}
                    >
                      {(creator.name || '?').slice(0, 1).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: 'var(--color-text-strong)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {creator.name}
                      </Typography>
                      <Box
                        sx={{
                          mt: 0.5,
                          height: 6,
                          borderRadius: 3,
                          bgcolor: 'rgba(var(--color-text-rgb), 0.08)',
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            width: `${pct}%`,
                            height: '100%',
                            bgcolor: accent,
                            transition: 'width 240ms ease-out',
                          }}
                        />
                      </Box>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color: 'var(--color-text-strong)',
                        minWidth: 24,
                        textAlign: 'right',
                      }}
                    >
                      {creator.count}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>

        {/* ── 5. Most viewed video project tags ── */}
        <Box sx={analyticsCardSx}>
          <AnalyticsCardHeader
            title="Most Viewed Project Tags"
            subtitle="Cumulative views of verified videos per project"
          />
          {mostViewedProjects.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
                No video views yet.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ height: Math.max(280, mostViewedProjects.length * 32 + 40) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={mostViewedProjects}
                  layout="vertical"
                  margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} horizontal={false} />
                  <XAxis
                    type="number"
                    allowDecimals={false}
                    stroke={chartAxisStroke}
                    tick={chartTickStyle}
                  />
                  <YAxis
                    type="category"
                    dataKey="label"
                    width={150}
                    stroke={chartAxisStroke}
                    tick={chartTickStyle}
                  />
                  <RechartsTooltip
                    content={<AnalyticsTooltip titleLabel="Project" />}
                  />
                  <Bar
                    dataKey="views"
                    name="Accumulated views"
                    radius={[0, 6, 6, 0]}
                    cursor="pointer"
                    onClick={(payload: unknown) => {
                      const p = payload as { id?: string; name?: string } | undefined;
                      if (!p?.id) return;
                      setDrillContext({
                        kind: 'project',
                        id: p.id,
                        label: p.name || p.id,
                      });
                    }}
                  >
                    {mostViewedProjects.map((proj, idx) => (
                      <Cell key={proj.id} fill={ACCENT_COLORS[(idx + 2) % ACCENT_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}
        </Box>

        {/* ── 6. Most shared video project tags ── */}
        <Box sx={analyticsCardSx}>
          <AnalyticsCardHeader
            title="Most Shared Project Tags"
            subtitle="Cumulative shares of verified videos per project"
          />
          {mostSharedProjects.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
                No video shares yet.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ height: Math.max(280, mostSharedProjects.length * 32 + 40) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={mostSharedProjects}
                  layout="vertical"
                  margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} horizontal={false} />
                  <XAxis
                    type="number"
                    allowDecimals={false}
                    stroke={chartAxisStroke}
                    tick={chartTickStyle}
                  />
                  <YAxis
                    type="category"
                    dataKey="label"
                    width={150}
                    stroke={chartAxisStroke}
                    tick={chartTickStyle}
                  />
                  <RechartsTooltip
                    content={<AnalyticsTooltip titleLabel="Project" />}
                  />
                  <Bar
                    dataKey="shares"
                    name="Accumulated shares"
                    radius={[0, 6, 6, 0]}
                    cursor="pointer"
                    onClick={(payload: unknown) => {
                      const p = payload as { id?: string; name?: string } | undefined;
                      if (!p?.id) return;
                      setDrillContext({
                        kind: 'project',
                        id: p.id,
                        label: p.name || p.id,
                      });
                    }}
                  >
                    {mostSharedProjects.map((proj, idx) => (
                      <Cell key={proj.id} fill={ACCENT_COLORS[(idx + 4) % ACCENT_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}
        </Box>
      </Box>

      <AnalyticsDrillDialog
        context={drillContext}
        videos={drillVideos}
        onClose={() => setDrillContext(null)}
      />
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// AnalyticsDrillDialog — modal list of videos shown when a user clicks a
// project bar or a creator row in the analytics tab. Keeps its own
// `activePlayback` state so nesting the existing
// `PodcastVideoPlayerDialog` doesn't collide with the manage dialog's
// player.
// ---------------------------------------------------------------------------

type AnalyticsDrillContext =
  | { kind: 'project'; id: string; label: string }
  | { kind: 'creator'; id: string; label: string }
  | { kind: 'status'; status: 'pending' | 'verified' | 'rejected' | 'all'; label: string };

interface AnalyticsDrillDialogProps {
  context: AnalyticsDrillContext | null;
  videos: PodcastVideoRecord[];
  onClose: () => void;
}

// Page size for the drill dialog's paginated list. 10 keeps each
// page comfortably scrollable on laptop screens without forcing the
// user to scroll past the rows to reach the pagination control.
const DRILL_PAGE_SIZE = 10;

const AnalyticsDrillDialog: React.FC<AnalyticsDrillDialogProps> = ({
  context,
  videos,
  onClose,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [activePlayback, setActivePlayback] = useState<PodcastVideoRecord | null>(
    null,
  );

  const open = context != null;
  const title = !context
    ? ''
    : context.kind === 'project'
      ? `Verified videos tagged to ${context.label}`
      : context.kind === 'creator'
        ? `Verified videos by ${context.label}`
        : context.label;
  const subtitle = !context
    ? ''
    : `${videos.length.toLocaleString()} video${videos.length === 1 ? '' : 's'}`;

  // Reset to page 1 whenever the drill context changes (i.e. the user
  // opened a different bar / row / card), or when the dialog closes.
  // Without this the dialog could reopen mid-list and show an empty
  // page if the new list is shorter than the previous page index.
  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
  }, [context]);

  const totalPages = Math.max(1, Math.ceil(videos.length / DRILL_PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedVideos = useMemo(
    () => videos.slice((safePage - 1) * DRILL_PAGE_SIZE, safePage * DRILL_PAGE_SIZE),
    [videos, safePage],
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen={fullScreen}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'var(--color-surface)',
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1,
            pr: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'var(--color-text-strong)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'var(--color-text-muted)' }}
            >
              {subtitle}
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Close" size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ bgcolor: 'var(--color-bg)' }}>
          {videos.length === 0 ? (
            <Typography
              variant="body2"
              sx={{ color: 'var(--color-text-muted)', py: 4, textAlign: 'center' }}
            >
              No videos to show.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {pagedVideos.map((video) => {
                const src = resolveSource(video);
                const srcMeta = src ? getSourceMeta(src) : null;
                const status = video.status ?? 'pending';
                const statusColor = STATUS_COLORS[status];
                return (
                  <Box
                    key={video.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setActivePlayback(video)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setActivePlayback(video);
                      }
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1,
                      borderRadius: 1.5,
                      border: '1px solid rgba(var(--color-text-rgb), 0.08)',
                      bgcolor: 'var(--color-surface)',
                      cursor: 'pointer',
                      transition: 'background-color 120ms ease-out',
                      '&:hover': {
                        bgcolor: 'rgba(var(--color-text-rgb), 0.05)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        width: 96,
                        height: 64,
                        flexShrink: 0,
                        borderRadius: 1,
                        overflow: 'hidden',
                        bgcolor: '#000',
                      }}
                    >
                      {video.thumbnailUrl ? (
                        <Box
                          component="img"
                          src={video.thumbnailUrl}
                          alt=""
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'rgba(255,255,255,0.6)',
                          }}
                        >
                          <PlayCircleOutlineIcon />
                        </Box>
                      )}
                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'rgba(0,0,0,0.25)',
                          opacity: 0,
                          transition: 'opacity 120ms ease-out',
                          '&:hover': { opacity: 1 },
                          color: '#fff',
                        }}
                      >
                        <PlayCircleOutlineIcon fontSize="large" />
                      </Box>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: 'var(--color-text-strong)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {video.title || video.videoUrl}
                      </Typography>
                      <Box
                        sx={{
                          mt: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flexWrap: 'wrap',
                        }}
                      >
                        {srcMeta && (
                          <Chip
                            size="small"
                            icon={
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  color: '#fff !important',
                                }}
                              >
                                {srcMeta.icon}
                              </Box>
                            }
                            label={srcMeta.label}
                            sx={{
                              bgcolor: srcMeta.color,
                              color: '#fff',
                              fontWeight: 600,
                              height: 20,
                              '& .MuiChip-icon': { ml: 0.5, color: '#fff' },
                              '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' },
                            }}
                          />
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 0.75,
                            py: 0.15,
                            borderRadius: 0.75,
                            bgcolor: `${statusColor}22`,
                            color: statusColor,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                          }}
                        >
                          {status}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'var(--color-text-muted)' }}
                        >
                          {video.createdByName || video.createdBy || '—'}
                          {video.createdAt
                            ? ` • ${formatCreatedDate(video.createdAt)}`
                            : ''}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={totalPages}
                page={safePage}
                onChange={(_, value) => setPage(value)}
                color="primary"
                size={fullScreen ? 'small' : 'medium'}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <PodcastVideoPlayerDialog
        record={activePlayback}
        onClose={() => setActivePlayback(null)}
      />
    </>
  );
};

const ManagePodcastVideosDialog: React.FC<ManagePodcastVideosDialogProps> = ({
  open,
  onClose,
  isAdmin = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tab, setTab] = useState<DialogTab>('pending');
  // Analytics is a passive tab with no row list of its own; the
  // body short-circuits to a placeholder when this is `true` so we
  // don't accidentally index the per-status caches with 'analytics'.
  const isAnalyticsTab = tab === 'analytics';
  // Use the most recently active status tab when computing derived
  // values — keeps the row caches addressable even while analytics
  // is on screen (the values just aren't rendered).
  const statusTab: StatusTab = isAnalyticsTab ? 'pending' : tab;

  // Keep separate caches per tab so switching back and forth doesn't
  // refetch unnecessarily. Each tab tracks its own loading + error.
  const [pendingRows, setPendingRows] = useState<PodcastVideoRecord[]>([]);
  const [verifiedRows, setVerifiedRows] = useState<PodcastVideoRecord[]>([]);
  const [rejectedRows, setRejectedRows] = useState<PodcastVideoRecord[]>([]);
  const [loading, setLoading] = useState<Record<StatusTab, boolean>>({
    pending: false,
    verified: false,
    rejected: false,
  });
  const [loadError, setLoadError] = useState<Record<StatusTab, string | null>>({
    pending: null,
    verified: null,
    rejected: null,
  });

  // Selection lives only in the Pending tab. Cleared whenever we
  // reload that tab or after a successful review action.
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // `submitting` covers both verify + reject so the action bar can
  // disable itself uniformly while either request is in flight.
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Reject prompt sub-dialog state — the rejection reason is
  // required, so we collect it in a small modal before firing the
  // POST. `null` means the prompt is closed.
  const [rejectPromptOpen, setRejectPromptOpen] = useState<boolean>(false);
  const [rejectReason, setRejectReason] = useState<string>('');

  // Verify prompt sub-dialog state — clicking the Verify action bar
  // now opens a yes/no question asking whether the selected video(s)
  // were filmed in the company podcast room. The admin's choice is
  // forwarded as `isPodcastRoom` to the verify endpoint so the
  // public feed can surface room-recorded clips first.
  const [verifyPromptOpen, setVerifyPromptOpen] = useState<boolean>(false);

  // Player dialog state — opening it does not interfere with the
  // selection set, so admins can preview a clip and then continue
  // ticking boxes.
  const [activeRecord, setActiveRecord] = useState<PodcastVideoRecord | null>(null);

  // Setter for the per-tab cache. Centralised so the load + action
  // handlers don't all repeat the same switch.
  const setRowsForTab = useCallback(
    (which: StatusTab, rows: PodcastVideoRecord[]) => {
      if (which === 'pending') setPendingRows(rows);
      else if (which === 'verified') setVerifiedRows(rows);
      else setRejectedRows(rows);
    },
    [],
  );

  // Search + project filter mirror the My Podcasts dialog so admins
  // get a consistent triage UX. Filtering is done client-side against
  // the loaded set.
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filterProjectIds, setFilterProjectIds] = useState<string[]>([]);
  // Verified-tab-only podcast-room breakdown. Lets an admin narrow the
  // verified list to room-recorded clips, non-room clips, or all.
  const [verifiedRoomFilter, setVerifiedRoomFilter] = useState<'all' | 'room' | 'other'>('all');
  // Surface-partition filter (podcast vs training). Defaults to 'all'
  // so admins triaging the queue continue to see every row by
  // default; selecting a specific type lets them focus on one
  // surface at a time.
  const [typeFilter, setTypeFilter] = useState<'all' | 'podcast' | 'training'>('all');
  const [projectOptions, setProjectOptions] = useState<TopViewingSitePlan[]>([]);
  const [projectOptionsLoading, setProjectOptionsLoading] = useState<boolean>(false);
  const projectsLoadStartedRef = useRef<boolean>(false);

  // Training type filter is admin-only; VIDEO-APPROVER stays on all/podcast.
  useEffect(() => {
    if (!isAdmin && typeFilter === 'training') setTypeFilter('all');
  }, [isAdmin, typeFilter]);

  // Analytics tab is admin-only; kick VIDEO-APPROVER back to Pending.
  useEffect(() => {
    if (!isAdmin && tab === 'analytics') setTab('pending');
  }, [isAdmin, tab]);

  // Page index per tab. Reset whenever the search / project filter
  // changes (so users don't end up on an out-of-range page).
  const [pageByTab, setPageByTab] = useState<Record<StatusTab, number>>({
    pending: 1,
    verified: 1,
    rejected: 1,
  });

  const loadTab = useCallback(
    async (which: StatusTab) => {
      setLoading((prev) => ({ ...prev, [which]: true }));
      setLoadError((prev) => ({ ...prev, [which]: null }));
      try {
        const rows = await api.listPodcastVideosByStatus(which);
        setRowsForTab(which, rows);
        if (which === 'pending') setSelectedIds(new Set());
      } catch (err) {
        setLoadError((prev) => ({
          ...prev,
          [which]: err instanceof Error ? err.message : 'Failed to load podcast videos',
        }));
      } finally {
        setLoading((prev) => ({ ...prev, [which]: false }));
      }
    },
    [setRowsForTab],
  );

  // Eager-load ALL THREE tabs whenever the dialog opens so every
  // tab's count badge is accurate on first paint and the tab-switch
  // is instant. Cheap enough at typical sizes; revisit with paged
  // counts if any tab grows past a few hundred rows.
  useEffect(() => {
    if (!open) return;
    loadTab('pending');
    loadTab('verified');
    loadTab('rejected');
  }, [open, loadTab]);

  // Lazy-load the project list once when the dialog first opens.
  // Mirrors the same ref-guarded pattern used in My Podcasts to avoid
  // re-firing on state-change re-renders.
  useEffect(() => {
    if (!open) return;
    if (projectsLoadStartedRef.current) return;
    projectsLoadStartedRef.current = true;
    setProjectOptionsLoading(true);
    api
      .getTopViewingSitePlans()
      .then((rows) => setProjectOptions(rows))
      .catch(() => {
        // Best-effort — filter dropdown will just be empty.
      })
      .finally(() => setProjectOptionsLoading(false));
  }, [open]);

  // Reset transient state when the dialog closes so re-opening is
  // a clean slate.
  useEffect(() => {
    if (!open) {
      setTab('pending');
      setSelectedIds(new Set());
      setActionError(null);
      setSearchQuery('');
      setFilterProjectIds([]);
      setVerifiedRoomFilter('all');
      setTypeFilter('all');
      setPageByTab({ pending: 1, verified: 1, rejected: 1 });
      setRejectPromptOpen(false);
      setRejectReason('');
      setVerifyPromptOpen(false);
    }
  }, [open]);

  // Reset every tab's page when the search / filter changes so the
  // user never ends up on a now-empty trailing page.
  useEffect(() => {
    setPageByTab({ pending: 1, verified: 1, rejected: 1 });
  }, [searchQuery, filterProjectIds, verifiedRoomFilter, typeFilter]);

  // Apply the active search + project filters to a list of rows.
  // Pulled out as a stable function so both tab arrays go through the
  // same predicate.
  const applyFilters = useCallback(
    (rows: PodcastVideoRecord[]): PodcastVideoRecord[] => {
      const needle = searchQuery.trim().toLowerCase();
      return rows.filter((v) => {
        if (typeFilter !== 'all') {
          const rowType = v.videoType ?? 'podcast';
          if (rowType !== typeFilter) return false;
        }
        if (filterProjectIds.length > 0) {
          const tagged = (v.projects ?? []).map((p) => p.id);
          if (!filterProjectIds.some((pid) => tagged.includes(pid))) return false;
        }
        if (needle) {
          const haystack = [
            v.title ?? '',
            v.videoUrl,
            v.videoSource,
            v.createdByName ?? '',
            v.createdBy,
            v.reviewNote ?? '',
            ...(v.projects ?? []).map((p) => p.name),
          ]
            .join(' ')
            .toLowerCase();
          if (!haystack.includes(needle)) return false;
        }
        return true;
      });
    },
    [searchQuery, filterProjectIds, typeFilter],
  );

  const filteredPending = useMemo(
    () => applyFilters(pendingRows),
    [applyFilters, pendingRows],
  );
  const filteredVerified = useMemo(() => {
    const base = applyFilters(verifiedRows);
    if (verifiedRoomFilter === 'all') return base;
    if (verifiedRoomFilter === 'room') return base.filter((v) => v.isPodcastRoom === true);
    return base.filter((v) => v.isPodcastRoom !== true);
  }, [applyFilters, verifiedRows, verifiedRoomFilter]);
  const filteredRejected = useMemo(
    () => applyFilters(rejectedRows),
    [applyFilters, rejectedRows],
  );

  const filteredCurrentRows =
    statusTab === 'pending'
      ? filteredPending
      : statusTab === 'verified'
        ? filteredVerified
        : filteredRejected;
  const currentLoading = loading[statusTab];
  const currentError = loadError[statusTab];

  // Slice the current tab's filtered set into the visible page. Clamp
  // the page so a stale `pageByTab` value never produces an empty
  // page mid-render.
  const currentPage = pageByTab[statusTab];
  const totalPages = Math.max(1, Math.ceil(filteredCurrentRows.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pagedRows = useMemo(
    () =>
      filteredCurrentRows.slice(
        (safePage - 1) * PAGE_SIZE,
        safePage * PAGE_SIZE,
      ),
    [filteredCurrentRows, safePage],
  );

  const toggleSelected = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // "Select all" operates on the filtered set so admins can narrow
  // the pending list with the search/project filter and then
  // bulk-action the matching rows.
  const allFilteredSelected = useMemo(
    () =>
      filteredPending.length > 0 &&
      filteredPending.every((v) => selectedIds.has(v.id)),
    [filteredPending, selectedIds],
  );

  const handleSelectAll = useCallback(() => {
    if (allFilteredSelected) {
      // Deselect only the rows currently in view (i.e. the filtered
      // set) — preserve any selections from rows hidden by the
      // current filter.
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filteredPending.forEach((v) => next.delete(v.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        filteredPending.forEach((v) => next.add(v.id));
        return next;
      });
    }
  }, [allFilteredSelected, filteredPending]);

  /** Opens the podcast-room confirmation prompt. */
  const openVerifyPrompt = useCallback(() => {
    if (submitting) return;
    if (selectedIds.size === 0) return;
    setActionError(null);
    setVerifyPromptOpen(true);
  }, [selectedIds, submitting]);

  /**
   * Submits the verify POST with the admin's podcast-room answer.
   * Called from the verify-prompt sub-dialog's Yes / No buttons.
   */
  const confirmVerify = useCallback(
    async (isPodcastRoom: boolean) => {
      if (submitting) return;
      const ids = Array.from(selectedIds);
      if (ids.length === 0) return;
      setSubmitting(true);
      setActionError(null);
      try {
        const result = await api.verifyPodcastVideos(ids, isPodcastRoom);
        const movedIdSet = new Set(result.reviewedIds);
        // Move verified rows out of the pending cache and into the
        // verified cache (front of the list to mirror the CreatedAt
        // DESC ordering of a fresh fetch). The stamped review audit
        // fields come from the response so the moved rows surface
        // immediately with the right "Verified by ... on ..." caption.
        const flag = result.isPodcastRoom ?? isPodcastRoom;
        const moved = pendingRows
          .filter((v) => movedIdSet.has(v.id))
          .map<PodcastVideoRecord>((v) => ({
            ...v,
            status: 'verified',
            reviewedAt: result.reviewedAt,
            reviewedBy: result.reviewedBy,
            reviewedByName: result.reviewedByName,
            reviewNote: null,
            isPodcastRoom: flag,
          }));
        setPendingRows((prev) => prev.filter((v) => !movedIdSet.has(v.id)));
        setVerifiedRows((prev) => [...moved, ...prev]);
        setSelectedIds(new Set());
        setVerifyPromptOpen(false);
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Failed to verify');
      } finally {
        setSubmitting(false);
      }
    },
    [selectedIds, pendingRows, submitting],
  );

  /** Opens the rejection-reason prompt. */
  const openRejectPrompt = useCallback(() => {
    if (submitting) return;
    if (selectedIds.size === 0) return;
    setActionError(null);
    setRejectReason('');
    setRejectPromptOpen(true);
  }, [selectedIds, submitting]);

  /** Submits the rejection once the admin has typed a non-empty reason. */
  const confirmReject = useCallback(async () => {
    if (submitting) return;
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    const note = rejectReason.trim();
    if (!note) {
      setActionError('A rejection reason is required.');
      return;
    }
    setSubmitting(true);
    setActionError(null);
    try {
      const result = await api.rejectPodcastVideos(ids, note);
      const movedIdSet = new Set(result.reviewedIds);
      const moved = pendingRows
        .filter((v) => movedIdSet.has(v.id))
        .map<PodcastVideoRecord>((v) => ({
          ...v,
          status: 'rejected',
          reviewedAt: result.reviewedAt,
          reviewedBy: result.reviewedBy,
          reviewedByName: result.reviewedByName,
          // Prefer the echoed note (already trimmed + sliced server-side)
          // so the local cache matches what's persisted.
          reviewNote: result.reviewNote ?? note,
        }));
      setPendingRows((prev) => prev.filter((v) => !movedIdSet.has(v.id)));
      setRejectedRows((prev) => [...moved, ...prev]);
      setSelectedIds(new Set());
      setRejectPromptOpen(false);
      setRejectReason('');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to reject');
    } finally {
      setSubmitting(false);
    }
  }, [selectedIds, pendingRows, rejectReason, submitting]);

  return (
    <>
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="lg"
      fullScreen={isMobile}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
          fontWeight: 700,
        }}
      >
        Manage Videos
        <IconButton
          onClick={onClose}
          disabled={submitting}
          size="small"
          aria-label="Close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, value: DialogTab) => setTab(value)}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab
            value="pending"
            label={`Pending${
              filteredPending.length > 0 ? ` (${filteredPending.length})` : ''
            }`}
          />
          <Tab
            value="verified"
            label={`Verified${
              filteredVerified.length > 0 ? ` (${filteredVerified.length})` : ''
            }`}
          />
          <Tab
            value="rejected"
            label={`Rejected${
              filteredRejected.length > 0 ? ` (${filteredRejected.length})` : ''
            }`}
          />
          {isAdmin && <Tab value="analytics" label="Analytics" />}
        </Tabs>
      </Box>

      <DialogContent dividers sx={{ bgcolor: 'var(--color-bg)' }}>
        {isAnalyticsTab ? (
          <PodcastAnalyticsPanel
            pendingRows={pendingRows}
            verifiedRows={verifiedRows}
            rejectedRows={rejectedRows}
            loading={loading.pending || loading.verified || loading.rejected}
            isAdmin={isAdmin}
          />
        ) : (
        <>
        {/* Search + project filter — same controls as the My Podcasts
            dialog so admins triaging the queue have a consistent
            experience. Filters apply to both tabs simultaneously. */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <TextField
            size="small"
            placeholder="Search by title, URL, source, submitter, or project"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              width: { xs: '100%', sm: '50%' },
              bgcolor: 'var(--color-surface)',
            }}
          />
          <Autocomplete
            multiple
            disableCloseOnSelect
            size="small"
            loading={projectOptionsLoading}
            options={projectOptions}
            sx={{
              width: { xs: '100%', sm: '50%' },
              bgcolor: 'var(--color-surface)',
            }}
            value={filterProjectIds
              .map((id) => projectOptions.find((p) => p.projectId === id))
              .filter((p): p is TopViewingSitePlan => Boolean(p))}
            onChange={(_, value) =>
              setFilterProjectIds(value.map((p) => p.projectId))
            }
            getOptionLabel={(opt) => opt.projectName || opt.projectId}
            isOptionEqualToValue={(option, value) => option.projectId === value.projectId}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={
                  filterProjectIds.length === 0 ? 'Filter by project(s)' : ''
                }
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((opt, idx) => (
                <Chip
                  {...getTagProps({ index: idx })}
                  key={opt.projectId}
                  label={opt.projectName || opt.projectId}
                  size="small"
                />
              ))
            }
          />
        </Stack>

        {/* Surface-partition filter — applies to every tab so admins
            can focus on either the public Podcast Videos surface or
            the per-project Training Videos surface. Defaults to all. */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ mb: 2, flexWrap: 'wrap' }}
        >
          <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
            Type
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={typeFilter}
            onChange={(_, value: 'all' | 'podcast' | 'training' | null) => {
              if (value) setTypeFilter(value);
            }}
            aria-label="Filter videos by type"
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontWeight: 600,
                px: 1.5,
                py: 0.4,
              },
            }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="podcast">Podcast</ToggleButton>
            {isAdmin && <ToggleButton value="training">Training</ToggleButton>}
          </ToggleButtonGroup>
        </Stack>

        {/* Verified-only podcast-room breakdown filter */}
        {tab === 'verified' && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ mb: 2, flexWrap: 'wrap' }}
          >
            <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
              Show
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={verifiedRoomFilter}
              onChange={(_, value: 'all' | 'room' | 'other' | null) => {
                if (value) setVerifiedRoomFilter(value);
              }}
              aria-label="Filter verified videos by podcast room"
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 1.5,
                  py: 0.4,
                },
              }}
            >
              <ToggleButton value="all">All videos</ToggleButton>
              <ToggleButton value="room">Podcast room videos</ToggleButton>
              <ToggleButton value="other">Other videos</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        )}

        {/* Pending-only action bar */}
        {tab === 'pending' && (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            spacing={1.5}
            sx={{ mb: 2 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox
                size="small"
                checked={allFilteredSelected}
                indeterminate={selectedIds.size > 0 && !allFilteredSelected}
                onChange={handleSelectAll}
                disabled={filteredPending.length === 0 || submitting}
              />
              <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
                {selectedIds.size > 0
                  ? `${selectedIds.size} selected`
                  : 'Select all'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {actionError && (
                <Typography variant="caption" sx={{ color: 'error.main' }}>
                  {actionError}
                </Typography>
              )}
              {/* Reject — opens a sub-dialog that collects the required
                  rejection reason before firing the POST. */}
              <Button
                variant="outlined"
                color="error"
                disabled={selectedIds.size === 0 || submitting}
                onClick={openRejectPrompt}
                startIcon={<CancelOutlinedIcon />}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Reject
              </Button>
              <Button
                variant="contained"
                disabled={selectedIds.size === 0 || submitting}
                onClick={openVerifyPrompt}
                startIcon={
                  submitting ? (
                    <CircularProgress size={16} thickness={5} sx={{ color: '#fff' }} />
                  ) : (
                    <VerifiedOutlinedIcon />
                  )
                }
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  bgcolor: 'var(--color-accent)',
                  '&:hover': {
                    bgcolor: 'var(--color-accent-strong, var(--color-accent))',
                  },
                }}
              >
                Verify
              </Button>
            </Box>
          </Stack>
        )}

        {/* Body */}
        {currentLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : currentError ? (
          <Typography
            variant="body2"
            sx={{ color: 'error.main', py: 4, textAlign: 'center' }}
          >
            {currentError}
          </Typography>
        ) : filteredCurrentRows.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: 'var(--color-text-muted)', py: 6, textAlign: 'center' }}
          >
            {(tab === 'pending'
              ? pendingRows
              : tab === 'verified'
                ? verifiedRows
                : rejectedRows
            ).length === 0
              ? tab === 'pending'
                ? 'No podcast videos pending review.'
                : tab === 'verified'
                  ? 'No verified podcast videos yet.'
                  : 'No rejected podcast videos.'
              : 'No podcast videos match the current filters.'}
          </Typography>
        ) : (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(3, minmax(0, 1fr))',
                },
                gap: 2,
              }}
            >
              {pagedRows.map((record) => (
                <ManagePodcastCard
                  key={record.id}
                  record={record}
                  selectable={tab === 'pending'}
                  selected={selectedIds.has(record.id)}
                  onToggle={() => toggleSelected(record.id)}
                  onPlay={() => setActiveRecord(record)}
                  disabled={submitting}
                />
              ))}
            </Box>
            {/* Pagination — only render when there's more than one
                page so a small list doesn't end up with a lonely
                disabled control. */}
            {totalPages > 1 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 2.5,
                }}
              >
                <Pagination
                  count={totalPages}
                  page={safePage}
                  onChange={(_, value) =>
                    setPageByTab((prev) => ({ ...prev, [statusTab]: value }))
                  }
                  color="primary"
                  size={isMobile ? 'small' : 'medium'}
                />
              </Box>
            )}
          </>
        )}
        </>
        )}
      </DialogContent>
    </Dialog>

    {/* Reject reason prompt — small sub-dialog with a required
        multiline `TextField`. The note is sent to the backend and
        surfaced verbatim on the submitter's My Podcasts Rejected tab,
        so we lean on the textarea rather than a single-line input. */}
    <Dialog
      open={rejectPromptOpen}
      onClose={submitting ? undefined : () => setRejectPromptOpen(false)}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle sx={{ fontWeight: 700 }}>
        Reject {selectedIds.size} podcast video{selectedIds.size === 1 ? '' : 's'}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', mb: 1.5 }}>
          Tell the submitter why their podcast video was rejected. This
          message will appear in their My Podcasts dialog. Rejection is
          final — to undo, the submitter must delete the row and
          re-submit.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          maxRows={8}
          inputProps={{ maxLength: 500 }}
          placeholder="e.g. Video is unrelated to the tagged project; please re-submit with the correct project tag."
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          disabled={submitting}
          helperText={`${rejectReason.length} / 500`}
        />
        {actionError && (
          <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 1 }}>
            {actionError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setRejectPromptOpen(false)}
          disabled={submitting}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={confirmReject}
          disabled={submitting || !rejectReason.trim()}
          startIcon={
            submitting ? (
              <CircularProgress size={16} thickness={5} sx={{ color: '#fff' }} />
            ) : (
              <CancelOutlinedIcon />
            )
          }
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          Reject
        </Button>
      </DialogActions>
    </Dialog>

    {/* Verify prompt — confirms whether the selected clip(s) were filmed
        in the company podcast room. The admin's Yes / No answer is
        forwarded as `isPodcastRoom` to the verify endpoint so the public
        feed can surface room-recorded clips first. Both answers still
        verify the row(s); only the flag differs. */}
    <Dialog
      open={verifyPromptOpen}
      onClose={submitting ? undefined : () => setVerifyPromptOpen(false)}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
          fontWeight: 700,
        }}
      >
        Verify {selectedIds.size} podcast video{selectedIds.size === 1 ? '' : 's'}
        <IconButton
          onClick={() => setVerifyPromptOpen(false)}
          disabled={submitting}
          size="small"
          aria-label="Close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ color: 'var(--color-text-muted)' }}>
          Were the selected podcast video{selectedIds.size === 1 ? ' was' : 's were'}{' '}
          filmed in the company podcast room? Room-recorded clips are
          surfaced first on the public feed.
        </Typography>
        {actionError && (
          <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 1 }}>
            {actionError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={() => confirmVerify(false)}
          disabled={submitting}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          No
        </Button>
        <Button
          variant="contained"
          onClick={() => confirmVerify(true)}
          disabled={submitting}
          startIcon={
            submitting ? (
              <CircularProgress size={16} thickness={5} sx={{ color: '#fff' }} />
            ) : (
              <VerifiedOutlinedIcon />
            )
          }
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            bgcolor: 'var(--color-accent)',
            '&:hover': {
              bgcolor: 'var(--color-accent-strong, var(--color-accent))',
            },
          }}
        >
          Yes, from podcast room
        </Button>
      </DialogActions>
    </Dialog>

    {/* Shared player dialog — renders on top of this dialog when an
        admin clicks a thumbnail. Closing the player leaves selection
        state untouched. */}
    <PodcastVideoPlayerDialog
      record={activeRecord}
      onClose={() => setActiveRecord(null)}
    />
    </>
  );
};

export default ManagePodcastVideosDialog;

// ---------------------------------------------------------------------------
// ManagePodcastCard
// ---------------------------------------------------------------------------

interface ManagePodcastCardProps {
  record: PodcastVideoRecord;
  selectable: boolean;
  selected: boolean;
  onToggle: () => void;
  onPlay: () => void;
  disabled: boolean;
}

const ManagePodcastCard: React.FC<ManagePodcastCardProps> = ({
  record,
  selectable,
  selected,
  onToggle,
  onPlay,
  disabled,
}) => {
  const source: PodcastSource = sourceFromUrl(record.videoUrl) ?? 'youtube';
  const sourceMeta = getSourceMeta(source);
  const title = record.title || record.videoUrl;
  const projects = record.projects ?? [];
  // Card attribution line.
  //  - Verified rows: "Verified by {admin name} on {reviewedAt}"
  //  - Rejected rows: "Rejected by {admin name} on {reviewedAt}"
  //                   + the rejection note rendered separately below.
  //  - Pending rows : "Submitted by {submitter name} on {createdAt}"
  // Each branch falls back to the CEA when the joined User row is
  // missing, and silently drops " on {date}" when the timestamp is
  // null so we never render a dangling " on ".
  const attributionLine = (() => {
    if (record.status === 'verified' || record.status === 'rejected') {
      const reviewerName =
        record.reviewedByName || record.reviewedBy || 'an admin';
      const reviewedDate = formatCreatedDate(record.reviewedAt ?? null);
      const verb = record.status === 'verified' ? 'Verified' : 'Rejected';
      return reviewedDate
        ? `${verb} by ${reviewerName} on ${reviewedDate}`
        : `${verb} by ${reviewerName}`;
    }
    const submitterName = record.createdByName || record.createdBy;
    const submittedDate = formatCreatedDate(record.createdAt);
    return submittedDate
      ? `Submitted by ${submitterName} on ${submittedDate}`
      : `Submitted by ${submitterName}`;
  })();

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        border: '1px solid',
        borderColor: selected ? 'var(--color-accent)' : 'var(--color-border-strong)',
        boxShadow: selected ? 'inset 0 0 0 1px var(--color-accent)' : 'none',
        bgcolor: 'var(--color-surface)',
        overflow: 'hidden',
        transition: 'border-color 120ms ease, box-shadow 120ms ease',
      }}
    >
      {/* Selection checkbox overlay */}
      {selectable && (
        <Box
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            zIndex: 2,
            bgcolor: 'rgba(255,255,255,0.85)',
            borderRadius: '50%',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            size="small"
            checked={selected}
            onChange={onToggle}
            disabled={disabled}
          />
        </Box>
      )}

      {/* Thumbnail — the click target for opening the player. The
          selection checkbox sits in an overlay above and stops
          propagation, so an admin can preview a clip without ticking
          it (or vice versa). */}
      <Box
        component="button"
        type="button"
        onClick={onPlay}
        aria-label={`Play ${title}`}
        sx={{
          position: 'relative',
          width: '100%',
          height: CARD_THUMB_HEIGHT,
          bgcolor: '#000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 0,
          padding: 0,
          cursor: 'pointer',
          '&:hover .play-overlay': { opacity: 1 },
          '&:focus-visible': {
            outline: '2px solid var(--color-accent)',
            outlineOffset: -2,
          },
        }}
      >
        {record.thumbnailUrl ? (
          <Box
            component="img"
            src={record.thumbnailUrl}
            alt={title}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <PlayCircleOutlineIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.6)' }} />
        )}
        <Box
          className="play-overlay"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.25)',
            opacity: 0,
            transition: 'opacity 120ms ease',
            pointerEvents: 'none',
          }}
        >
          <PlayCircleOutlineIcon
            sx={{ fontSize: 48, color: 'rgba(255,255,255,0.95)' }}
          />
        </Box>
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 0.75,
            py: 0.25,
            borderRadius: 1,
            bgcolor: sourceMeta.color,
            color: '#fff',
            fontSize: '0.7rem',
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {sourceMeta.icon}
          {sourceMeta.label}
        </Box>
        {/* Surface-partition badge — sits below the source badge so
            admins can tell at a glance whether a row belongs to the
            public Podcast Videos feed or the per-project Training
            Videos dialog. */}
        <Box
          sx={{
            position: 'absolute',
            top: 36,
            left: 8,
            px: 0.75,
            py: 0.2,
            borderRadius: 1,
            bgcolor:
              (record.videoType ?? 'podcast') === 'training'
                ? 'rgba(16, 185, 129, 0.9)'
                : 'rgba(99, 102, 241, 0.9)',
            color: '#fff',
            fontSize: '0.65rem',
            fontWeight: 700,
            lineHeight: 1.2,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}
        >
          {(record.videoType ?? 'podcast') === 'training' ? 'Training' : 'Podcast'}
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', flex: 1, gap: 0.75 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            color: 'var(--color-text-strong)',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            wordBreak: 'break-word',
            lineHeight: 1.3,
          }}
          title={title}
        >
          {title}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: 'var(--color-text-muted)', display: 'block' }}
        >
          {attributionLine}
        </Typography>
        {/* Rejection reason — only on rejected rows. Clipped to two
            lines on the card; the full text is available as a
            native tooltip on hover. */}
        {record.status === 'rejected' && record.reviewNote && (
          <Typography
            variant="caption"
            title={record.reviewNote}
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              p: 0.75,
              borderRadius: 1,
              bgcolor: 'rgba(211, 47, 47, 0.06)',
              color: '#c62828',
              fontWeight: 500,
            }}
          >
            <strong>Reason:</strong> {record.reviewNote}
          </Typography>
        )}
        {projects.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {projects.map((p) => (
              <Chip key={p.id} size="small" label={p.name} sx={{ height: 20 }} />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};
